// POS System Types - Separate from existing Sales types

export enum POSPaymentMethod {
  CASH = 'CASH',
  CARD_PAYMENT = 'CARD_PAYMENT',
  MANUAL_CARD = 'MANUAL_CARD',
  MOBILE_TRANSFER = 'MOBILE_TRANSFER',
  OULA_CARD = 'OULA_CARD',
  CREDIT = 'CREDIT',
  CARI_ORDER = 'CARI_ORDER',
  /** Returned by API when multiple payment methods were used */
  SPLIT = 'SPLIT',
}

export interface POSCartItem {
  id: string; // Temporary ID for cart items
  product_id: number;
  product_code?: string;
  product_name: string;
  barcode?: string;
  /** Quantity in sell units (pcs / packs / cartons). */
  quantity: number;
  /** Catalog / list price per sell unit (unchanged when offering a line discount). */
  unit_price: number;
  /** Actual price charged per sell unit (defaults to list). */
  sale_unit_price: number;
  total_price: number;
  stock_quantity?: number; // Base units available
  gst_category?: string;
  gst_rate?: number;
  gst_amount?: number;
  /** Retail or wholesale list used for this line. */
  price_mode?: 'retail' | 'wholesale';
  /** How quantity is counted on the till. */
  sell_unit?: 'base' | 'pack' | 'carton' | 'bag';
  /** Base units (pcs) per one sell unit. */
  units_per?: number;
  /** Snapshot for repricing when mode/unit changes. */
  packaging?: {
    price: number;
    wholesale_price?: number | null;
    pack_size?: number | null;
    carton_size?: number | null;
    pack_selling_price?: number | null;
    pack_wholesale_price?: number | null;
    carton_selling_price?: number | null;
    carton_wholesale_price?: number | null;
    bag_size?: number | null;
    bag_selling_price?: number | null;
    bag_wholesale_price?: number | null;
  };
  /** True when cashier manually edited the charged unit price. */
  price_locked?: boolean;
}

export interface POSCoupon {
  id: number;
  code: string;
  name: string;
  discount_type: 'PERCENTAGE' | 'FIXED';
  discount_value: number;
  min_purchase?: number;
  max_discount?: number;
}

export interface POSStashedTransaction {
  id: string;
  serial: string;
  time: string;
  items: POSCartItem[];
  total_amount: number;
  created_at: string;
}

export interface POSPayment {
  method: POSPaymentMethod;
  amount: number;
  reference_number?: string; // Bank journal number for mobile transfers
  payer_phone?: string; // Enquiry phone if credit not received
}

/** One line of a split payment at checkout */
export interface POSPaymentSplitLine {
  id: string;
  method: POSPaymentMethod;
  amount: string;
  reference: string; // Bank journal number
  phone: string; // Payer phone for mobile lines
}

export interface POSPaymentDetailRecord {
  id?: number;
  payment_method: string;
  amount: number;
  reference_number?: string | null;
  payer_phone?: string | null;
}

export interface POSTransaction {
  id: number;
  transaction_number: string;
  serial: string;
  location_id: number;
  location_name?: string;
  sale_date: string;
  sale_time: string;
  payment_method: POSPaymentMethod;
  payment_reference?: string;
  payer_phone?: string;
  /** Whether a receipt was printed / given for this sale */
  receipt_printed?: boolean;
  total_amount: number;
  tax_amount: number;
  discount_amount: number;
  coupon_discount?: number;
  served_by: number;
  cashier_name?: string;
  created_at: string;
  items: POSCartItem[];
  status?: string; // COMPLETED, REFUNDED, CANCELLED
  is_refund?: boolean;
  payment_details?: POSPaymentDetailRecord[];
  is_tax_invoice?: boolean;
  tax_invoice_number?: string | null;
  taxable_amount?: number | null;
  exempt_amount?: number | null;
  zero_rated_amount?: number | null;
  buyer_name?: string | null;
  buyer_cid?: string | null;
  buyer_tpn?: string | null;
  buyer_address?: string | null;
  supplier_name?: string | null;
  supplier_tpn?: string | null;
  supplier_address?: string | null;
  gst_prices_inclusive?: boolean | null;
}

export interface POSProduct {
  id: number;
  code: string;
  name: string;
  barcode?: string;
  price: number;
  wholesale_price?: number | null;
  pack_size?: number | null;
  carton_size?: number | null;
  pack_selling_price?: number | null;
  pack_wholesale_price?: number | null;
  carton_selling_price?: number | null;
  carton_wholesale_price?: number | null;
  bag_size?: number | null;
  bag_selling_price?: number | null;
  bag_wholesale_price?: number | null;
  stock_quantity?: number;
  category?: string;
  image_url?: string;
  gst_category?: string;
  gst_rate?: number;
  manufacture_date?: string | null;
  expiry_date?: string | null;
  expiry_alert_days?: number;
  days_to_expiry?: number | null;
  expiry_status?: 'expired' | 'expiring_soon' | 'ok' | 'no_date';
  expiry_label?: string;
}

export interface POSQuickCategory {
  id: string;
  name: string;
  products: POSProduct[];
}
